// Video card component for search results

import type { SearchVideo } from "@dishsnap/shared";
import styles from "./VideoCard.module.css";

interface VideoCardProps {
  video: SearchVideo;
  isSelected: boolean;
  onToggleSelect: (video: SearchVideo) => void;
  disabled?: boolean;
  isUsed?: boolean; // Video already extracted (exists in jobs)
}

export function VideoCard({ video, isSelected, onToggleSelect, disabled, isUsed }: VideoCardProps) {
  const handleClick = () => {
    if (!disabled) {
      onToggleSelect(video);
    }
  };

  return (
    <div
      className={`${styles.card} ${isSelected ? styles.selected : ""} ${disabled ? styles.disabled : ""} ${isUsed ? styles.used : ""}`}
      onClick={handleClick}
    >
      <div className={styles.checkbox}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(video)}
          disabled={disabled}
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      <div className={styles.thumbnail}>
        <img src={video.cover} alt={video.title} />
        <div className={styles.duration}>{formatDuration(video.duration)}</div>
        {isUsed && <div className={styles.usedBadge}>Extracted</div>}
      </div>

      <div className={styles.content}>
        <h3 className={styles.title}>{video.title}</h3>

        <div className={styles.author}>
          {video.author.avatar && (
            <img
              src={video.author.avatar}
              alt={video.author.nickname}
              className={styles.avatar}
            />
          )}
          <span>@{video.author.uniqueId}</span>
        </div>

        <div className={styles.stats}>
          <span title="Views">{formatCount(video.playCount)} views</span>
          <span title="Likes">{formatCount(video.diggCount)} likes</span>
        </div>

        <div className={styles.date}>
          {formatDate(video.createTime)}
        </div>
      </div>
    </div>
  );
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function formatCount(count: number): string {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)}M`;
  }
  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1)}K`;
  }
  return count.toString();
}

function formatDate(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}
